import React, { useCallback, useEffect, useRef, useState } from 'react';
import { PageHeader } from './ui/PageHeader';
import Button from './ui/Button';
import Modal from './ui/Modal';
import ConfirmDialog from './ui/ConfirmDialog';
import { AdminQueueState } from './ui/AdminQueueState';
import { useToast } from '../contexts/ToastContext';
import {
  adminGetPendingPerformanceRecords,
  adminGetModerationShops,
  adminGetContributorDisputes,
  adminGetFactorySpecs,
  adminVerifyPerformanceRecord,
  adminInvalidatePerformanceRecord,
  adminSetShopStatus,
  adminSetContributorVerified,
  adminRevokeContributorClaim,
  adminHideMediaImage,
  adminSaveFactorySpec,
  adminDeleteFactorySpec,
} from '../services/api/adminExpansion';
import type {
  AdminPendingPerformanceRecord,
  AdminModerationShop,
  AdminContributorDispute,
  AdminFactorySpec,
  FactorySpecInput,
} from '../services/api/adminExpansion';

type TabKey = 'performance' | 'shops' | 'contributors' | 'factory' | 'media';

const TABS: Array<{ key: TabKey; label: string }> = [
  { key: 'performance', label: 'Performance records' },
  { key: 'shops', label: 'Shops' },
  { key: 'contributors', label: 'Contributor disputes' },
  { key: 'factory', label: 'Factory specs' },
  { key: 'media', label: 'Media' },
];

type PendingConfirm =
  | { kind: 'verify_record'; record: AdminPendingPerformanceRecord }
  | { kind: 'invalidate_record'; record: AdminPendingPerformanceRecord }
  | { kind: 'suspend_shop'; shop: AdminModerationShop }
  | { kind: 'revoke_contributor'; contributor: AdminContributorDispute }
  | { kind: 'delete_spec'; spec: AdminFactorySpec }
  | { kind: 'hide_media'; imageId: number };

interface SpecForm {
  year: string;
  make: string;
  model: string;
  trim: string;
  power_hp: string;
  torque_lbft: string;
  weight_lbs: string;
  engine: string;
  transmission: string;
  drivetrain: string;
  forced_induction: string;
  source: string;
  is_estimated: boolean;
}

const emptySpecForm: SpecForm = {
  year: '',
  make: '',
  model: '',
  trim: '',
  power_hp: '',
  torque_lbft: '',
  weight_lbs: '',
  engine: '',
  transmission: '',
  drivetrain: '',
  forced_induction: '',
  source: '',
  is_estimated: false,
};

const prettyLabel = (value: string): string =>
  value.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());

const formatDate = (value: string | null): string => {
  if (!value) return '-';
  const parsed = new Date(value.replace(' ', 'T'));
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString();
};

const fieldClass =
  'w-full bg-[#0a0a0a] border border-white/[0.08] rounded-lg px-3 py-2 text-gray-200 min-h-[44px] focus-visible:outline focus-visible:outline-2 focus-visible:outline-orange-400';

const shopStatusClass = (status: string): string => {
  switch (status) {
    case 'verified':
      return 'text-green-300';
    case 'suspended':
      return 'text-red-300';
    case 'pending':
      return 'text-amber-300';
    default:
      return 'text-gray-400';
  }
};

export const AdminExpansionModeration: React.FC = () => {
  const { showToast } = useToast();
  const [activeTab, setActiveTab] = useState<TabKey>('performance');
  const tabRefs = useRef<Partial<Record<TabKey, HTMLButtonElement | null>>>({});

  const [records, setRecords] = useState<AdminPendingPerformanceRecord[]>([]);
  const [shops, setShops] = useState<AdminModerationShop[]>([]);
  const [contributors, setContributors] = useState<AdminContributorDispute[]>([]);
  const [specs, setSpecs] = useState<AdminFactorySpec[]>([]);
  const [loadedTabs, setLoadedTabs] = useState<Partial<Record<TabKey, boolean>>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busyKey, setBusyKey] = useState<string | null>(null);

  const [specMake, setSpecMake] = useState('');
  const [specModel, setSpecModel] = useState('');

  const [pendingConfirm, setPendingConfirm] = useState<PendingConfirm | null>(null);

  const [specEditorOpen, setSpecEditorOpen] = useState(false);
  const [specForm, setSpecForm] = useState<SpecForm>(emptySpecForm);
  const [specEditingId, setSpecEditingId] = useState<number | null>(null);
  const [specFormError, setSpecFormError] = useState<string | null>(null);
  const [savingSpec, setSavingSpec] = useState(false);

  const [mediaImageId, setMediaImageId] = useState('');

  const loadTab = useCallback(
    async (tab: TabKey, force = false) => {
      if (tab === 'media') return;
      setLoading(true);
      setError(null);
      try {
        if (tab === 'performance') {
          setRecords(await adminGetPendingPerformanceRecords());
        } else if (tab === 'shops') {
          setShops(await adminGetModerationShops());
        } else if (tab === 'contributors') {
          setContributors(await adminGetContributorDisputes());
        } else if (tab === 'factory') {
          setSpecs(await adminGetFactorySpecs(specMake.trim() || undefined, specModel.trim() || undefined));
        }
        setLoadedTabs((prev) => ({ ...prev, [tab]: true }));
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : 'Failed to load moderation data');
      } finally {
        setLoading(false);
      }
    },
    [specMake, specModel]
  );

  useEffect(() => {
    if (activeTab !== 'media' && !loadedTabs[activeTab]) {
      void loadTab(activeTab);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- load once per newly activated tab
  }, [activeTab]);

  const selectTab = (tab: TabKey) => {
    setActiveTab(tab);
    setError(null);
  };

  const onTabKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>, index: number) => {
    if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
    e.preventDefault();
    const delta = e.key === 'ArrowRight' ? 1 : -1;
    const next = TABS[(index + delta + TABS.length) % TABS.length];
    selectTab(next.key);
    tabRefs.current[next.key]?.focus();
  };

  const runAction = async (key: string, action: () => Promise<void>, successMessage: string) => {
    setBusyKey(key);
    try {
      await action();
      showToast(successMessage, 'success');
    } catch (err: unknown) {
      showToast(err instanceof Error ? err.message : 'Action failed', 'error');
    } finally {
      setBusyKey(null);
    }
  };

  const confirmAction = async () => {
    if (!pendingConfirm) return;
    try {
      if (pendingConfirm.kind === 'verify_record') {
        await adminVerifyPerformanceRecord(pendingConfirm.record.id, 'admin_verified');
        showToast('Record verified.', 'success');
        await loadTab('performance', true);
      } else if (pendingConfirm.kind === 'invalidate_record') {
        await adminInvalidatePerformanceRecord(pendingConfirm.record.id);
        showToast('Record invalidated and archived.', 'success');
        await loadTab('performance', true);
      } else if (pendingConfirm.kind === 'suspend_shop') {
        await adminSetShopStatus(pendingConfirm.shop.id, 'suspended');
        showToast(`${pendingConfirm.shop.name} suspended.`, 'success');
        await loadTab('shops', true);
      } else if (pendingConfirm.kind === 'revoke_contributor') {
        await adminRevokeContributorClaim(pendingConfirm.contributor.id);
        showToast('Contributor claim removed.', 'success');
        await loadTab('contributors', true);
      } else if (pendingConfirm.kind === 'delete_spec') {
        await adminDeleteFactorySpec(pendingConfirm.spec.id);
        showToast('Factory spec deleted.', 'success');
        await loadTab('factory', true);
      } else if (pendingConfirm.kind === 'hide_media') {
        const hidden = await adminHideMediaImage(pendingConfirm.imageId);
        showToast(
          hidden ? `Image ${pendingConfirm.imageId} hidden.` : 'No matching active image was found.',
          hidden ? 'success' : 'warning'
        );
        setMediaImageId('');
      }
      setPendingConfirm(null);
    } catch (err: unknown) {
      showToast(err instanceof Error ? err.message : 'Action failed', 'error');
      setPendingConfirm(null);
    }
  };

  const confirmCopy = (): { title: string; description: string; confirmLabel: string; destructive: boolean } => {
    if (!pendingConfirm) {
      return { title: '', description: '', confirmLabel: 'Confirm', destructive: false };
    }
    switch (pendingConfirm.kind) {
      case 'verify_record':
        return {
          title: 'Verify performance record',
          description: `Mark "${pendingConfirm.record.record_type_label}" for ${pendingConfirm.record.vehicle_label} as admin verified? Verified records count toward achievements.`,
          confirmLabel: 'Verify record',
          destructive: false,
        };
      case 'invalidate_record':
        return {
          title: 'Invalidate performance record',
          description: `Invalidate and archive "${pendingConfirm.record.record_type_label}" for ${pendingConfirm.record.vehicle_label}? The record will no longer appear on the build page.`,
          confirmLabel: 'Invalidate',
          destructive: true,
        };
      case 'suspend_shop':
        return {
          title: 'Suspend shop',
          description: `Suspend ${pendingConfirm.shop.name}? Suspended shops are hidden from the public directory until restored.`,
          confirmLabel: 'Suspend',
          destructive: true,
        };
      case 'revoke_contributor':
        return {
          title: 'Remove contributor claim',
          description: `Remove this ${pendingConfirm.contributor.contributor_type} claim from ${
            pendingConfirm.contributor.nickname ||
            `${pendingConfirm.contributor.year ?? ''} ${pendingConfirm.contributor.make ?? ''} ${pendingConfirm.contributor.model ?? ''}`.trim()
          }? The credit will be revoked.`,
          confirmLabel: 'Remove claim',
          destructive: true,
        };
      case 'delete_spec':
        return {
          title: 'Delete factory spec',
          description: `Delete the factory catalog entry for ${pendingConfirm.spec.year} ${pendingConfirm.spec.make} ${pendingConfirm.spec.model}${pendingConfirm.spec.trim ? ` ${pendingConfirm.spec.trim}` : ''}? This cannot be undone.`,
          confirmLabel: 'Delete',
          destructive: true,
        };
      case 'hide_media':
        return {
          title: 'Hide media',
          description: `Hide image #${pendingConfirm.imageId} everywhere on the platform? The owner keeps the file but it stops rendering publicly.`,
          confirmLabel: 'Hide image',
          destructive: true,
        };
      default:
        return { title: '', description: '', confirmLabel: 'Confirm', destructive: false };
    }
  };

  const setShopStatus = (shop: AdminModerationShop, status: 'verified' | 'unverified') => {
    void runAction(
      `shop-${shop.id}`,
      async () => {
        await adminSetShopStatus(shop.id, status);
        await loadTab('shops', true);
      },
      status === 'verified' ? `${shop.name} verified.` : `${shop.name} restored to unverified.`
    );
  };

  const setContributorVerified = (row: AdminContributorDispute, verified: boolean) => {
    void runAction(
      `contrib-${row.id}`,
      async () => {
        await adminSetContributorVerified(row.id, verified);
        await loadTab('contributors', true);
      },
      verified ? 'Contributor claim admin verified.' : 'Admin verification removed.'
    );
  };

  const openCreateSpec = () => {
    setSpecEditingId(null);
    setSpecForm(emptySpecForm);
    setSpecFormError(null);
    setSpecEditorOpen(true);
  };

  const openEditSpec = (spec: AdminFactorySpec) => {
    setSpecEditingId(spec.id);
    setSpecForm({
      year: String(spec.year),
      make: spec.make,
      model: spec.model,
      trim: spec.trim,
      power_hp: spec.power_hp !== null ? String(spec.power_hp) : '',
      torque_lbft: spec.torque_lbft !== null ? String(spec.torque_lbft) : '',
      weight_lbs: spec.weight_lbs !== null ? String(spec.weight_lbs) : '',
      engine: spec.engine ?? '',
      transmission: spec.transmission ?? '',
      drivetrain: spec.drivetrain ?? '',
      forced_induction: spec.forced_induction ?? '',
      source: spec.source ?? '',
      is_estimated: spec.is_estimated,
    });
    setSpecFormError(null);
    setSpecEditorOpen(true);
  };

  const setSpecField = <K extends keyof SpecForm>(key: K, value: SpecForm[K]) => {
    setSpecForm((prev) => ({ ...prev, [key]: value }));
  };

  const saveSpec = async (e: React.FormEvent) => {
    e.preventDefault();
    setSpecFormError(null);
    const year = Number(specForm.year);
    if (!Number.isInteger(year) || year < 1900 || year > 2100) {
      setSpecFormError('Year must be between 1900 and 2100.');
      return;
    }
    if (!specForm.make.trim() || !specForm.model.trim()) {
      setSpecFormError('Make and model are required.');
      return;
    }
    const payload: FactorySpecInput = {
      year,
      make: specForm.make.trim(),
      model: specForm.model.trim(),
      trim: specForm.trim.trim(),
      power_hp: specForm.power_hp.trim() || null,
      torque_lbft: specForm.torque_lbft.trim() || null,
      weight_lbs: specForm.weight_lbs.trim() || null,
      engine: specForm.engine.trim() || null,
      transmission: specForm.transmission.trim() || null,
      drivetrain: specForm.drivetrain.trim() || null,
      forced_induction: specForm.forced_induction.trim() || null,
      source: specForm.source.trim() || null,
      is_estimated: specForm.is_estimated,
    };
    setSavingSpec(true);
    try {
      const result = await adminSaveFactorySpec(payload);
      showToast(result.created ? 'Factory spec created.' : 'Factory spec updated.', 'success');
      setSpecEditorOpen(false);
      await loadTab('factory', true);
    } catch (err: unknown) {
      setSpecFormError(err instanceof Error ? err.message : 'Failed to save factory spec');
    } finally {
      setSavingSpec(false);
    }
  };

  const requestHideMedia = (e: React.FormEvent) => {
    e.preventDefault();
    const imageId = Number(mediaImageId);
    if (!Number.isInteger(imageId) || imageId <= 0) {
      showToast('Enter a valid image ID.', 'error');
      return;
    }
    setPendingConfirm({ kind: 'hide_media', imageId });
  };

  const confirm = confirmCopy();

  return (
    <div className="space-y-6 max-w-6xl">
      <PageHeader
        eyebrow="Admin"
        title="Expansion moderation"
        description="Review performance records, shops, contributor claims, factory specs, and unsafe media."
      />

      <div role="tablist" aria-label="Moderation queues" className="flex flex-wrap gap-1 border-b border-white/[0.06] pb-px">
        {TABS.map((tab, index) => {
          const selected = activeTab === tab.key;
          return (
            <button
              key={tab.key}
              ref={(el) => {
                tabRefs.current[tab.key] = el;
              }}
              type="button"
              role="tab"
              id={`moderation-tab-${tab.key}`}
              aria-selected={selected}
              aria-controls={`moderation-panel-${tab.key}`}
              tabIndex={selected ? 0 : -1}
              onClick={() => selectTab(tab.key)}
              onKeyDown={(e) => onTabKeyDown(e, index)}
              className={`px-3 py-2.5 text-sm rounded-t-lg min-h-[44px] focus-visible:outline focus-visible:outline-2 focus-visible:outline-orange-400 ${
                selected
                  ? 'text-white bg-[#141414]/80 border border-white/[0.08] border-b-transparent font-medium'
                  : 'text-gray-400 hover:text-gray-200'
              }`}
            >
              {tab.label}
            </button>
          );
        })}
      </div>

      {error ? (
        <AdminQueueState
          variant="error"
          title="Could not load moderation queue"
          description={error}
          onRetry={() => void loadTab(activeTab, true)}
        />
      ) : null}

      {loading ? (
        <p className="text-gray-400 text-sm" role="status">
          Loading…
        </p>
      ) : null}

      {/* ─── Performance records ─────────────────────────────────────────── */}
      <div
        role="tabpanel"
        id="moderation-panel-performance"
        aria-labelledby="moderation-tab-performance"
        hidden={activeTab !== 'performance'}
      >
        {activeTab === 'performance' && !loading ? (
          records.length === 0 && !error ? (
            <AdminQueueState
              variant="empty"
              title="No performance records waiting for review"
              description="Submitted track times and dyno runs will appear here for verification."
            />
          ) : (
            <div className="overflow-x-auto rounded-xl border border-white/[0.08]">
              <table className="min-w-full text-sm">
                <caption className="sr-only">Performance records pending verification</caption>
                <thead className="bg-[#141414]/80 text-gray-400 text-left">
                  <tr>
                    <th scope="col" className="px-4 py-3 font-medium">Vehicle</th>
                    <th scope="col" className="px-4 py-3 font-medium">Record</th>
                    <th scope="col" className="px-4 py-3 font-medium">Value</th>
                    <th scope="col" className="px-4 py-3 font-medium">Venue / Shop</th>
                    <th scope="col" className="px-4 py-3 font-medium">Recorded</th>
                    <th scope="col" className="px-4 py-3 font-medium">Status</th>
                    <th scope="col" className="px-4 py-3 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/[0.06]">
                  {records.map((r) => (
                    <tr key={r.id}>
                      <td className="px-4 py-3">
                        <p className="text-white">{r.vehicle_label}</p>
                        {r.owner_username ? (
                          <p className="text-gray-500 text-xs">@{r.owner_username}</p>
                        ) : null}
                      </td>
                      <td className="px-4 py-3 text-gray-300">
                        {r.record_type_label}
                        {r.title ? <p className="text-gray-500 text-xs">{r.title}</p> : null}
                      </td>
                      <td className="px-4 py-3 text-gray-300 tabular-nums whitespace-nowrap">
                        {r.display_value ?? '-'}
                        {r.unit_primary ? ` ${r.unit_primary}` : ''}
                      </td>
                      <td className="px-4 py-3 text-gray-400 text-xs">
                        {[r.venue_name, r.shop_name].filter(Boolean).join(' / ') || '-'}
                      </td>
                      <td className="px-4 py-3 text-gray-400 text-xs whitespace-nowrap">
                        {formatDate(r.date_recorded)}
                      </td>
                      <td className="px-4 py-3 text-gray-300">{prettyLabel(r.verification_status)}</td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1.5">
                          <Button
                            type="button"
                            variant="secondary"
                            size="sm"
                            onClick={() => setPendingConfirm({ kind: 'verify_record', record: r })}
                          >
                            Verify
                          </Button>
                          <Button
                            type="button"
                            variant="danger"
                            size="sm"
                            onClick={() => setPendingConfirm({ kind: 'invalidate_record', record: r })}
                          >
                            Invalidate
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        ) : null}
      </div>

      {/* ─── Shops ───────────────────────────────────────────────────────── */}
      <div
        role="tabpanel"
        id="moderation-panel-shops"
        aria-labelledby="moderation-tab-shops"
        hidden={activeTab !== 'shops'}
      >
        {activeTab === 'shops' && !loading ? (
          shops.length === 0 && !error ? (
            <AdminQueueState
              variant="empty"
              title="No shops pending moderation"
              description="Shop profiles flagged for review will appear in this tab."
            />
          ) : (
            <div className="overflow-x-auto rounded-xl border border-white/[0.08]">
              <table className="min-w-full text-sm">
                <caption className="sr-only">Shops with verification state</caption>
                <thead className="bg-[#141414]/80 text-gray-400 text-left">
                  <tr>
                    <th scope="col" className="px-4 py-3 font-medium">Shop</th>
                    <th scope="col" className="px-4 py-3 font-medium">Location</th>
                    <th scope="col" className="px-4 py-3 font-medium">Created by</th>
                    <th scope="col" className="px-4 py-3 font-medium">Builds</th>
                    <th scope="col" className="px-4 py-3 font-medium">Status</th>
                    <th scope="col" className="px-4 py-3 font-medium">Created</th>
                    <th scope="col" className="px-4 py-3 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/[0.06]">
                  {shops.map((s) => (
                    <tr key={s.id}>
                      <td className="px-4 py-3">
                        <p className="text-white">{s.name}</p>
                        <p className="text-gray-500 text-xs">{s.slug}</p>
                      </td>
                      <td className="px-4 py-3 text-gray-400 text-xs">
                        {[s.city, s.state].filter(Boolean).join(', ') || '-'}
                      </td>
                      <td className="px-4 py-3 text-gray-400 text-xs">
                        {s.creator_username ? `@${s.creator_username}` : '-'}
                      </td>
                      <td className="px-4 py-3 text-gray-300 tabular-nums">{s.build_count}</td>
                      <td className="px-4 py-3">
                        <span className={shopStatusClass(s.verification_status)}>
                          {prettyLabel(s.verification_status)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-400 text-xs whitespace-nowrap">
                        {formatDate(s.created_at)}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1.5">
                          {s.verification_status !== 'verified' && s.verification_status !== 'suspended' ? (
                            <Button
                              type="button"
                              variant="secondary"
                              size="sm"
                              loading={busyKey === `shop-${s.id}`}
                              onClick={() => setShopStatus(s, 'verified')}
                            >
                              Verify
                            </Button>
                          ) : null}
                          {s.verification_status === 'suspended' ? (
                            <Button
                              type="button"
                              variant="secondary"
                              size="sm"
                              loading={busyKey === `shop-${s.id}`}
                              onClick={() => setShopStatus(s, 'unverified')}
                            >
                              Restore
                            </Button>
                          ) : (
                            <Button
                              type="button"
                              variant="danger"
                              size="sm"
                              onClick={() => setPendingConfirm({ kind: 'suspend_shop', shop: s })}
                            >
                              Suspend
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        ) : null}
      </div>

      {/* ─── Contributor disputes ────────────────────────────────────────── */}
      <div
        role="tabpanel"
        id="moderation-panel-contributors"
        aria-labelledby="moderation-tab-contributors"
        hidden={activeTab !== 'contributors'}
      >
        {activeTab === 'contributors' && !loading ? (
          contributors.length === 0 && !error ? (
            <AdminQueueState
              variant="empty"
              title="No contributor disputes in the queue"
              description="Disputed contributor claims will appear here for review."
            />
          ) : (
            <div className="overflow-x-auto rounded-xl border border-white/[0.08]">
              <table className="min-w-full text-sm">
                <caption className="sr-only">Pending and contested contributor claims</caption>
                <thead className="bg-[#141414]/80 text-gray-400 text-left">
                  <tr>
                    <th scope="col" className="px-4 py-3 font-medium">Vehicle</th>
                    <th scope="col" className="px-4 py-3 font-medium">Contributor</th>
                    <th scope="col" className="px-4 py-3 font-medium">Role</th>
                    <th scope="col" className="px-4 py-3 font-medium">Status</th>
                    <th scope="col" className="px-4 py-3 font-medium">Requested by</th>
                    <th scope="col" className="px-4 py-3 font-medium">Created</th>
                    <th scope="col" className="px-4 py-3 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/[0.06]">
                  {contributors.map((c) => {
                    const vehicleLabel =
                      c.nickname || `${c.year ?? ''} ${c.make ?? ''} ${c.model ?? ''}`.trim() || `#${c.vehicle_id}`;
                    const contributorLabel =
                      c.shop_name || c.display_name || (c.user_id ? `User #${c.user_id}` : 'Unknown');
                    return (
                      <tr key={c.id}>
                        <td className="px-4 py-3">
                          <p className="text-white">{vehicleLabel}</p>
                          {c.owner_username ? (
                            <p className="text-gray-500 text-xs">@{c.owner_username}</p>
                          ) : null}
                        </td>
                        <td className="px-4 py-3 text-gray-300">
                          {contributorLabel}
                          <p className="text-gray-500 text-xs">{prettyLabel(c.contributor_type)}</p>
                        </td>
                        <td className="px-4 py-3 text-gray-300">
                          {prettyLabel(c.role)}
                          {c.work_description ? (
                            <p className="text-gray-500 text-xs line-clamp-1">{c.work_description}</p>
                          ) : null}
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-amber-300">{prettyLabel(c.status)}</span>
                          {c.admin_verified ? (
                            <span className="block text-green-300 text-xs">Admin verified</span>
                          ) : null}
                        </td>
                        <td className="px-4 py-3 text-gray-400 text-xs">{prettyLabel(c.requested_by)}</td>
                        <td className="px-4 py-3 text-gray-400 text-xs whitespace-nowrap">
                          {formatDate(c.created_at)}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex flex-wrap gap-1.5">
                            <Button
                              type="button"
                              variant="secondary"
                              size="sm"
                              loading={busyKey === `contrib-${c.id}`}
                              onClick={() => setContributorVerified(c, !c.admin_verified)}
                            >
                              {c.admin_verified ? 'Remove admin verify' : 'Admin verify'}
                            </Button>
                            <Button
                              type="button"
                              variant="danger"
                              size="sm"
                              onClick={() => setPendingConfirm({ kind: 'revoke_contributor', contributor: c })}
                            >
                              Remove claim
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )
        ) : null}
      </div>

      {/* ─── Factory specs ───────────────────────────────────────────────── */}
      <div
        role="tabpanel"
        id="moderation-panel-factory"
        aria-labelledby="moderation-tab-factory"
        hidden={activeTab !== 'factory'}
      >
        {activeTab === 'factory' ? (
          <div className="space-y-4">
            <form
              onSubmit={(e) => {
                e.preventDefault();
                void loadTab('factory', true);
              }}
              className="flex flex-col sm:flex-row gap-2 sm:items-end"
            >
              <div className="flex-1">
                <label htmlFor="spec-filter-make" className="block text-sm text-gray-400 mb-1">
                  Make
                </label>
                <input
                  id="spec-filter-make"
                  type="text"
                  value={specMake}
                  onChange={(e) => setSpecMake(e.target.value)}
                  placeholder="Filter by make"
                  className={fieldClass}
                />
              </div>
              <div className="flex-1">
                <label htmlFor="spec-filter-model" className="block text-sm text-gray-400 mb-1">
                  Model
                </label>
                <input
                  id="spec-filter-model"
                  type="text"
                  value={specModel}
                  onChange={(e) => setSpecModel(e.target.value)}
                  placeholder="Filter by model"
                  className={fieldClass}
                />
              </div>
              <div className="flex gap-2">
                <Button type="submit" variant="secondary">
                  Search
                </Button>
                <Button type="button" variant="primary" onClick={openCreateSpec}>
                  Add spec
                </Button>
              </div>
            </form>

            {!loading ? (
              specs.length === 0 && !error ? (
                <AdminQueueState
                  variant={specMake || specModel ? 'filter-empty' : 'empty'}
                  title={specMake || specModel ? 'No factory specs match your filters' : 'No factory specs in the catalog'}
                  description={
                    specMake || specModel
                      ? 'Try different make or model filters, or add a new spec.'
                      : 'Add factory specs to power comparison data on build profiles.'
                  }
                  onClearFilter={specMake || specModel ? () => { setSpecMake(''); setSpecModel(''); } : undefined}
                />
              ) : (
                <div className="overflow-x-auto rounded-xl border border-white/[0.08]">
                  <table className="min-w-full text-sm">
                    <caption className="sr-only">Factory vehicle spec catalog entries</caption>
                    <thead className="bg-[#141414]/80 text-gray-400 text-left">
                      <tr>
                        <th scope="col" className="px-4 py-3 font-medium">Vehicle</th>
                        <th scope="col" className="px-4 py-3 font-medium">Power</th>
                        <th scope="col" className="px-4 py-3 font-medium">Torque</th>
                        <th scope="col" className="px-4 py-3 font-medium">Weight</th>
                        <th scope="col" className="px-4 py-3 font-medium">Engine</th>
                        <th scope="col" className="px-4 py-3 font-medium">Drivetrain</th>
                        <th scope="col" className="px-4 py-3 font-medium">Estimated</th>
                        <th scope="col" className="px-4 py-3 font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-white/[0.06]">
                      {specs.map((s) => (
                        <tr key={s.id}>
                          <td className="px-4 py-3">
                            <p className="text-white whitespace-nowrap">
                              {s.year} {s.make} {s.model}
                            </p>
                            {s.trim ? <p className="text-gray-500 text-xs">{s.trim}</p> : null}
                          </td>
                          <td className="px-4 py-3 text-gray-300 tabular-nums whitespace-nowrap">
                            {s.power_hp !== null ? `${s.power_hp} hp` : '-'}
                          </td>
                          <td className="px-4 py-3 text-gray-300 tabular-nums whitespace-nowrap">
                            {s.torque_lbft !== null ? `${s.torque_lbft} lb-ft` : '-'}
                          </td>
                          <td className="px-4 py-3 text-gray-300 tabular-nums whitespace-nowrap">
                            {s.weight_lbs !== null ? `${s.weight_lbs} lbs` : '-'}
                          </td>
                          <td className="px-4 py-3 text-gray-400 text-xs">
                            {s.engine ?? '-'}
                            {s.forced_induction ? (
                              <span className="block">{s.forced_induction}</span>
                            ) : null}
                          </td>
                          <td className="px-4 py-3 text-gray-400 text-xs">
                            {[s.drivetrain, s.transmission].filter(Boolean).join(' / ') || '-'}
                          </td>
                          <td className="px-4 py-3 text-gray-400 text-xs">
                            {s.is_estimated ? 'Estimated' : 'Confirmed'}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex flex-wrap gap-1.5">
                              <Button type="button" variant="ghost" size="sm" onClick={() => openEditSpec(s)}>
                                Edit
                              </Button>
                              <Button
                                type="button"
                                variant="danger"
                                size="sm"
                                onClick={() => setPendingConfirm({ kind: 'delete_spec', spec: s })}
                              >
                                Delete
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )
            ) : null}
          </div>
        ) : null}
      </div>

      {/* ─── Media ───────────────────────────────────────────────────────── */}
      <div
        role="tabpanel"
        id="moderation-panel-media"
        aria-labelledby="moderation-tab-media"
        hidden={activeTab !== 'media'}
      >
        {activeTab === 'media' ? (
          <div className="space-y-4 max-w-xl">
            <form
              onSubmit={requestHideMedia}
              className="border border-white/[0.08] rounded-xl p-4 bg-[#141414]/40 space-y-3"
            >
              <h2 className="text-white font-medium">Hide unsafe media</h2>
              <p className="text-gray-400 text-sm">
                Hide an uploaded image by its ID. The image stops rendering everywhere on the platform
                but the file is not deleted.
              </p>
              <div className="flex flex-col sm:flex-row gap-2">
                <label htmlFor="media-image-id" className="sr-only">
                  Image ID
                </label>
                <input
                  id="media-image-id"
                  type="number"
                  min={1}
                  inputMode="numeric"
                  value={mediaImageId}
                  onChange={(e) => setMediaImageId(e.target.value)}
                  placeholder="Image ID"
                  className={`${fieldClass} flex-1`}
                />
                <Button type="submit" variant="danger">
                  Hide image
                </Button>
              </div>
            </form>
            <p className="text-gray-500 text-sm">
              External video removal is not supported by this endpoint. Remove unsafe external videos
              from the vehicle media manager instead.
            </p>
          </div>
        ) : null}
      </div>

      {/* ─── Factory spec editor ─────────────────────────────────────────── */}
      <Modal
        isOpen={specEditorOpen}
        onClose={() => {
          if (!savingSpec) setSpecEditorOpen(false);
        }}
        title={specEditingId ? 'Edit factory spec' : 'Add factory spec'}
        description="Year, make, model, and trim identify the catalog entry. Saving an existing combination updates it."
        size="lg"
      >
        <form onSubmit={saveSpec} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label htmlFor="spec-year" className="block text-sm text-gray-400 mb-1">
                Year
              </label>
              <input
                id="spec-year"
                type="number"
                min={1900}
                max={2100}
                step={1}
                value={specForm.year}
                onChange={(e) => setSpecField('year', e.target.value)}
                className={fieldClass}
              />
            </div>
            <div>
              <label htmlFor="spec-make" className="block text-sm text-gray-400 mb-1">
                Make
              </label>
              <input
                id="spec-make"
                type="text"
                maxLength={80}
                value={specForm.make}
                onChange={(e) => setSpecField('make', e.target.value)}
                className={fieldClass}
              />
            </div>
            <div>
              <label htmlFor="spec-model" className="block text-sm text-gray-400 mb-1">
                Model
              </label>
              <input
                id="spec-model"
                type="text"
                maxLength={80}
                value={specForm.model}
                onChange={(e) => setSpecField('model', e.target.value)}
                className={fieldClass}
              />
            </div>
            <div>
              <label htmlFor="spec-trim" className="block text-sm text-gray-400 mb-1">
                Trim (optional)
              </label>
              <input
                id="spec-trim"
                type="text"
                maxLength={80}
                value={specForm.trim}
                onChange={(e) => setSpecField('trim', e.target.value)}
                className={fieldClass}
              />
            </div>
            <div>
              <label htmlFor="spec-power" className="block text-sm text-gray-400 mb-1">
                Power (hp)
              </label>
              <input
                id="spec-power"
                type="number"
                min={1}
                max={5000}
                step="any"
                value={specForm.power_hp}
                onChange={(e) => setSpecField('power_hp', e.target.value)}
                className={fieldClass}
              />
            </div>
            <div>
              <label htmlFor="spec-torque" className="block text-sm text-gray-400 mb-1">
                Torque (lb-ft)
              </label>
              <input
                id="spec-torque"
                type="number"
                min={1}
                max={5000}
                step="any"
                value={specForm.torque_lbft}
                onChange={(e) => setSpecField('torque_lbft', e.target.value)}
                className={fieldClass}
              />
            </div>
            <div>
              <label htmlFor="spec-weight" className="block text-sm text-gray-400 mb-1">
                Weight (lbs)
              </label>
              <input
                id="spec-weight"
                type="number"
                min={500}
                max={20000}
                step="any"
                value={specForm.weight_lbs}
                onChange={(e) => setSpecField('weight_lbs', e.target.value)}
                className={fieldClass}
              />
            </div>
            <div>
              <label htmlFor="spec-engine" className="block text-sm text-gray-400 mb-1">
                Engine
              </label>
              <input
                id="spec-engine"
                type="text"
                maxLength={120}
                value={specForm.engine}
                onChange={(e) => setSpecField('engine', e.target.value)}
                className={fieldClass}
              />
            </div>
            <div>
              <label htmlFor="spec-transmission" className="block text-sm text-gray-400 mb-1">
                Transmission
              </label>
              <input
                id="spec-transmission"
                type="text"
                maxLength={80}
                value={specForm.transmission}
                onChange={(e) => setSpecField('transmission', e.target.value)}
                className={fieldClass}
              />
            </div>
            <div>
              <label htmlFor="spec-drivetrain" className="block text-sm text-gray-400 mb-1">
                Drivetrain
              </label>
              <input
                id="spec-drivetrain"
                type="text"
                maxLength={20}
                value={specForm.drivetrain}
                onChange={(e) => setSpecField('drivetrain', e.target.value)}
                placeholder="RWD, AWD, FWD"
                className={fieldClass}
              />
            </div>
            <div>
              <label htmlFor="spec-fi" className="block text-sm text-gray-400 mb-1">
                Forced induction
              </label>
              <input
                id="spec-fi"
                type="text"
                maxLength={60}
                value={specForm.forced_induction}
                onChange={(e) => setSpecField('forced_induction', e.target.value)}
                placeholder="Turbo, supercharger, or none"
                className={fieldClass}
              />
            </div>
            <div>
              <label htmlFor="spec-source" className="block text-sm text-gray-400 mb-1">
                Source (optional)
              </label>
              <input
                id="spec-source"
                type="text"
                maxLength={200}
                value={specForm.source}
                onChange={(e) => setSpecField('source', e.target.value)}
                className={fieldClass}
              />
            </div>
          </div>

          <label className="flex items-center gap-2 text-sm text-gray-300">
            <input
              type="checkbox"
              checked={specForm.is_estimated}
              onChange={(e) => setSpecField('is_estimated', e.target.checked)}
              className="w-4 h-4"
            />
            Values are estimated
          </label>

          {specFormError ? (
            <p role="alert" className="text-red-300 text-sm">
              {specFormError}
            </p>
          ) : null}

          <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
            <Button type="button" variant="ghost" disabled={savingSpec} onClick={() => setSpecEditorOpen(false)}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" loading={savingSpec} loadingLabel="Saving…">
              {specEditingId ? 'Save changes' : 'Create spec'}
            </Button>
          </div>
        </form>
      </Modal>

      <ConfirmDialog
        isOpen={Boolean(pendingConfirm)}
        title={confirm.title}
        description={confirm.description}
        confirmLabel={confirm.confirmLabel}
        destructive={confirm.destructive}
        loadingLabel="Working…"
        onConfirm={confirmAction}
        onCancel={() => setPendingConfirm(null)}
      />
    </div>
  );
};

export default AdminExpansionModeration;
